/**
 * `<caos-record-field-inspector>` — every field of one record as a row: API name, label, type, value.
 * (Catalog: Developer surfaces › RecordFieldInspector — one record as a flat, sortable, in-place editable list of all its fields.)
 *
 * A record page shows the fields somebody decided to lay out. This shows ALL of them, flat, with
 * their API names, and that is the whole difference: the question it answers is "what is actually on
 * this record", which a layout is specifically designed not to answer.
 *
 * EDITING IS EXPLICIT. A cell takes an edit and the edit sits there, marked, until Save or Discard.
 * Nothing is written on blur. That is the opposite of the inline editing on a record page, and
 * deliberately so — a developer poking at four fields of a live record wants to see the whole set of
 * changes before any of them lands, and an auto-save surface makes a typo into a write.
 *
 * WHAT SAVE CARRIES is only the fields that changed, not the record. A component that handed back
 * every field it was given would make every save a full overwrite, which quietly clobbers whatever
 * anybody else changed in between.
 *
 * A READ-ONLY FIELD HAS NO EDITOR, and says why in its own row rather than only being inert. There
 * are three reasons a field cannot be written here — the platform computes it, this person lacks
 * the permission, or the value is masked — and they need different things from the reader, so
 * collapsing them into a greyed-out box would be throwing away the only useful part.
 *
 * SORTING BY ANY OF THE FOUR is not decoration: looking for a field by API name, by label, by type
 * and by which ones have values in them are four different searches people genuinely run, and a
 * fixed order serves at most one of them.
 *
 * Styling seams: `::part(inspector)`, `::part(toolbar)`, `::part(search)`, `::part(actions)`,
 * `::part(table)`, `::part(summary)`, `::part(value-input)`, `::part(reason)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import type { CaosDataTable, CaosSortDirection, CaosTableRow } from './data-table.js';

/** Why a field cannot be edited here. Absent ⇒ it can. */
export type CaosFieldLock = 'computed' | 'no-permission' | 'masked';

/** How each lock reads to a person. Exported so a surface can say the same thing elsewhere. */
export const FIELD_LOCK_REASONS: Readonly<Record<CaosFieldLock, string>> = {
  computed: 'Computed by the platform',
  'no-permission': 'You cannot edit this field',
  masked: 'Value hidden',
};

/** One field of the record. */
export interface CaosRecordField {
  /** The API name — the identity, and what events key on. */
  readonly apiName: string;
  /** The human label. */
  readonly label: string;
  /** The field's type, as a word a developer recognises. */
  readonly type: string;
  /** The current value. `null`/`undefined` is an empty field, which is not the same as an empty string. */
  readonly value?: string | number | boolean | null;
  /** Why it cannot be edited here. Absent ⇒ it can. */
  readonly lock?: CaosFieldLock;
}

/** Which column the rows are ordered by. */
export type CaosFieldSortKey = 'apiName' | 'label' | 'type' | 'value';

const sheet = makeSheet(`
  :host { display: block; }

  .inspector { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--caos-space-3);
    flex-wrap: wrap;
  }

  .search {
    flex: 1 1 200px;
    min-width: 160px;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .search:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .actions { display: inline-flex; gap: var(--caos-space-2); }

  .actions button {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 4px 10px;
    cursor: pointer;
  }
  .actions button:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .actions button:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .actions button.save:not(:disabled) {
    border-color: var(--caos-color-accent);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-weight: 600;
  }
  .actions button.delete:not(:disabled) { color: var(--caos-color-danger); border-color: var(--caos-color-danger); }

  .summary {
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
`);

/** Filter fields by a free-text term, over API name, label and type — the three a person searches. */
export function matchFields(fields: readonly CaosRecordField[], term: string): readonly CaosRecordField[] {
  const q = term.trim().toLowerCase();
  if (q === '') return fields;
  return fields.filter(
    (f) =>
      f.apiName.toLowerCase().includes(q) ||
      f.label.toLowerCase().includes(q) ||
      f.type.toLowerCase().includes(q),
  );
}

/** Order fields by one of the four columns, empties last — the same rule the result grid states. */
export function orderFields(
  fields: readonly CaosRecordField[],
  key: CaosFieldSortKey,
  direction: CaosSortDirection,
): readonly CaosRecordField[] {
  const sign = direction === 'desc' ? -1 : 1;
  const read = (f: CaosRecordField): string =>
    key === 'value' ? (f.value === null || f.value === undefined ? '' : String(f.value)) : String(f[key]);
  return [...fields].sort((a, b) => {
    const x = read(a);
    const y = read(b);
    if (x === '' && y === '') return a.apiName.localeCompare(b.apiName);
    if (x === '') return 1;
    if (y === '') return -1;
    const cmp = x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' });
    return (cmp !== 0 ? cmp : a.apiName.localeCompare(b.apiName)) * sign;
  });
}

export class CaosRecordFieldInspector extends CaosElement {
  static get observedAttributes(): string[] {
    return ['record-id', 'search', 'saving', 'deletable', 'sort-key', 'sort-direction'];
  }

  private searchEl: HTMLInputElement;
  private saveEl: HTMLButtonElement;
  private discardEl: HTMLButtonElement;
  private deleteEl: HTMLButtonElement;
  private exportEl: HTMLButtonElement;
  private summaryEl: HTMLParagraphElement;
  private tableEl: CaosDataTable;

  private data: readonly CaosRecordField[] = [];
  /** Edits not yet saved, keyed by API name. Empty is the resting state, not a null. */
  private edits = new Map<string, string>();

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'inspector';
    wrap.setAttribute('part', 'inspector');

    const toolbar = document.createElement('div');
    toolbar.className = 'toolbar';
    toolbar.setAttribute('part', 'toolbar');

    this.searchEl = document.createElement('input');
    this.searchEl.type = 'search';
    this.searchEl.className = 'search';
    this.searchEl.setAttribute('part', 'search');
    this.searchEl.placeholder = 'Search fields';
    this.searchEl.setAttribute('aria-label', 'Search fields by name, label or type');
    this.searchEl.addEventListener('input', () => {
      this.setAttribute('search', this.searchEl.value);
    });

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    this.saveEl = this.action('Save', 'save', () => this.save());
    this.discardEl = this.action('Discard', 'discard', () => this.discard());
    this.exportEl = this.action('Export', 'export', () => this.exportFields());
    this.deleteEl = this.action('Delete record', 'delete', () => this.requestDelete());
    actions.append(this.saveEl, this.discardEl, this.exportEl, this.deleteEl);

    toolbar.append(this.searchEl, actions);

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      // The ordering is this component's, over the fields it holds — the same argument the result
      // grid makes. It stops here rather than reaching an application that cannot act on it.
      e.stopPropagation();
      this.setAttribute('sort-key', column);
      this.setAttribute('sort-direction', direction);
    });

    wrap.append(toolbar, this.summaryEl, this.tableEl);
    this.root.append(wrap);
  }

  private action(label: string, className: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = className;
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, before: string | null, after: string | null): void {
    if (name === 'record-id' && before !== null && before !== after) {
      // A different record is a different set of edits. Carrying them over would write one record's
      // half-finished changes onto another, which is the single worst thing this surface could do.
      this.edits.clear();
    }
    if (name === 'search' && this.searchEl.value !== (after ?? '')) this.searchEl.value = after ?? '';
    this.render();
  }

  get fields(): readonly CaosRecordField[] {
    return this.data;
  }

  set fields(next: readonly CaosRecordField[]) {
    this.data = Array.isArray(next) ? next : [];
    // Edits for fields that are no longer here cannot be saved and must not be counted as pending.
    const known = new Set(this.data.map((f) => f.apiName));
    for (const key of [...this.edits.keys()]) if (!known.has(key)) this.edits.delete(key);
    this.render();
  }

  /** The changed fields, as `{ apiName: value }` — only what changed, never the whole record. */
  get pending(): Readonly<Record<string, string>> {
    return Object.fromEntries(this.edits);
  }

  get dirty(): boolean {
    return this.edits.size > 0;
  }

  private get sortKey(): CaosFieldSortKey {
    const asked = this.getAttribute('sort-key');
    return asked === 'label' || asked === 'type' || asked === 'value' ? asked : 'apiName';
  }

  private get sortDirection(): CaosSortDirection {
    return this.getAttribute('sort-direction') === 'desc' ? 'desc' : 'asc';
  }

  /** The fields being shown, after the search and in the current order. */
  get visibleFields(): readonly CaosRecordField[] {
    return orderFields(matchFields(this.data, this.getAttribute('search') ?? ''), this.sortKey, this.sortDirection);
  }

  private edit(field: CaosRecordField, input: HTMLInputElement, next: string): void {
    const original = field.value === null || field.value === undefined ? '' : String(field.value);
    // Typing a value and then typing the original back is not a change. Comparing rather than
    // latching a flag is what stops Save offering to write a record nobody altered.
    const changed = next !== original;
    if (changed) this.edits.set(field.apiName, next);
    else this.edits.delete(field.apiName);

    // Marked on the cell HERE rather than by re-rendering the rows. Rebuilding the table under a
    // keystroke would destroy the very input being typed into and take the caret with it, so the
    // one thing that has to change is changed in place.
    if (changed) input.setAttribute('data-changed', 'yes');
    else input.removeAttribute('data-changed');

    this.renderState();
    this.dispatchEvent(
      new CustomEvent('caos-field-edit', {
        bubbles: true,
        composed: true,
        detail: { apiName: field.apiName, value: next, dirty: this.dirty },
      }),
    );
  }

  private save(): void {
    if (!this.dirty || this.hasAttribute('saving')) return;
    this.dispatchEvent(
      new CustomEvent('caos-record-save', {
        bubbles: true,
        composed: true,
        detail: { recordId: this.getAttribute('record-id'), changes: this.pending },
      }),
    );
  }

  private discard(): void {
    if (!this.dirty) return;
    this.edits.clear();
    this.render();
    this.dispatchEvent(new CustomEvent('caos-record-discard', { bubbles: true, composed: true }));
  }

  /** Clear the pending edits — what a host calls once a save has actually landed. */
  accept(): void {
    this.edits.clear();
    this.render();
  }

  private exportFields(): void {
    this.dispatchEvent(
      new CustomEvent('caos-record-export', {
        bubbles: true,
        composed: true,
        detail: { recordId: this.getAttribute('record-id'), fields: this.visibleFields },
      }),
    );
  }

  private requestDelete(): void {
    // Asking, not deleting. Whether a record can be deleted, and what warning that deserves, is not
    // a component's call — and an irreversible act behind an unconfirmed click is not one either.
    this.dispatchEvent(
      new CustomEvent('caos-record-delete', {
        bubbles: true,
        composed: true,
        detail: { recordId: this.getAttribute('record-id') },
      }),
    );
  }

  private render(): void {
    this.tableEl.columns = [
      { key: 'apiName', label: 'API name', sortable: true, width: '26%' },
      { key: 'label', label: 'Label', sortable: true, width: '24%' },
      { key: 'type', label: 'Type', sortable: true, width: '16%' },
      { key: 'value', label: 'Value', sortable: true },
    ];
    this.tableEl.sort = { key: this.sortKey, direction: this.sortDirection };
    this.tableEl.rows = this.visibleFields.map((f) => this.rowFor(f));
    this.renderState();
  }

  private renderState(): void {
    const shown = this.visibleFields.length;
    const total = this.data.length;
    const changed = this.edits.size;
    const strong = document.createElement('strong');
    strong.textContent = shown === total ? `${total} fields` : `${shown} of ${total} fields`;
    const nodes: Node[] = [strong];
    if (changed > 0) {
      nodes.push(document.createTextNode(` · ${changed} unsaved ${changed === 1 ? 'change' : 'changes'}`));
    }
    this.summaryEl.replaceChildren(...nodes);

    const saving = this.hasAttribute('saving');
    this.saveEl.disabled = !this.dirty || saving;
    this.saveEl.textContent = saving ? 'Saving…' : 'Save';
    this.discardEl.disabled = !this.dirty || saving;
    this.exportEl.disabled = total === 0;
    this.deleteEl.hidden = !this.hasAttribute('deletable');
    this.deleteEl.disabled = saving;
  }

  private rowFor(field: CaosRecordField): CaosTableRow {
    const changed = this.edits.has(field.apiName);
    let valueCell: unknown;

    if (field.lock) {
      const span = document.createElement('span');
      span.setAttribute('part', 'reason');
      // Styled by the TABLE's sheet — the node lives in the table's shadow root, which this
      // component's stylesheet cannot reach. Same seam as the editable-cell rule below.
      span.className = 'caos-table__cell-note';
      // The value AND the reason: a greyed-out box says only that you cannot, which is the half a
      // reader already worked out from it not responding.
      const shown = field.lock === 'masked' ? '' : field.value === null || field.value === undefined ? '' : String(field.value);
      span.textContent = shown === '' ? FIELD_LOCK_REASONS[field.lock] : `${shown} — ${FIELD_LOCK_REASONS[field.lock]}`;
      valueCell = { node: span };
    } else {
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'caos-table__cell-edit';
      input.setAttribute('part', 'value-input');
      input.value = changed
        ? this.edits.get(field.apiName)!
        : field.value === null || field.value === undefined
          ? ''
          : String(field.value);
      input.setAttribute('aria-label', `${field.label} (${field.apiName})`);
      if (changed) input.setAttribute('data-changed', 'yes');
      input.addEventListener('input', () => this.edit(field, input, input.value));
      valueCell = { node: input };
    }

    return {
      id: field.apiName,
      cells: {
        apiName: field.apiName,
        label: field.label,
        type: field.type,
        value: valueCell,
      },
    } as CaosTableRow;
  }
}

define('caos-record-field-inspector', CaosRecordFieldInspector);

declare global {
  interface HTMLElementTagNameMap {
    'caos-record-field-inspector': CaosRecordFieldInspector;
  }
}
